/**
 * One short tone without an asset (feature plan 0073, the timer's end): a sine of 880 Hz for 0.4 s, quiet, faded
 * in and out against clicks. Web Audio only lets a page make sound after a user gesture, so:
 *
 * - `prime()` — call inside a gesture: creates the `AudioContext` and resumes it — always, even when `state` still
 *   reads 'running': after `suspend()` the attribute changes only later, and a resume asked after it wins
 *   (review 0073-03);
 * - `beep()` — creates the context late if the browser has already seen a gesture on the page
 *   (`navigator.userActivation.hasBeenActive`), resumes one that does not run or whose last request was a
 *   suspend (its `state` may still read 'running', review 0073-04) and waits for that (at most 500 ms);
 *   only a running context plays — otherwise the tone is dropped, never queued (a queued tone would sound
 *   minutes later, at the next gesture). Its promise settles only once the tone has ENDED (`onended`, or the
 *   tone's length as a fallback) — the owner must not suspend the context under a playing tone (review 0073-01);
 * - `suspend()` — the owner (the timer watcher) stops the audio thread when no timer runs; the beeper never
 *   suspends on its own. Also asked of a context that is still resuming — `suspend()` after a pending
 *   `resume()` wins, so the context does not keep running after all;
 * - `running()` — whether the context runs AND the last request was a resume that has settled — not `state`
 *   alone, which still reads 'running' right after a `suspend()` (the watcher keeps listening for a gesture until
 *   this is true).
 *
 * No Web Audio (old browsers, vitest), or a context the browser refused to create → silent; creating is tried
 * only once, not again at every gesture (review 0073-04). Every error, also an asynchronous one, ends in `console.warn`.
 */
export interface Beeper {
    prime(): void;
    beep(): Promise<void>;
    suspend(): void;
    running(): boolean;
}

const FREQUENCY_HZ = 880;
const DURATION_S = 0.4;
const FADE_S = 0.02;
const VOLUME = 0.15;
const RESUME_WAIT_MS = 500;
/** Fallback when `onended` does not come: the tone's length plus a little. */
const ENDED_FALLBACK_MS = DURATION_S * 1000 + 50;

function defaultFactory(): AudioContext | null {
    return typeof AudioContext === 'undefined' ? null : new AudioContext();
}

function hasBeenActive(): boolean {
    return typeof navigator !== 'undefined'
        && (navigator as Navigator & {userActivation?: {hasBeenActive?: boolean}}).userActivation?.hasBeenActive === true;
}

export function createBeeper(factory: () => AudioContext | null = defaultFactory): Beeper {
    let context: AudioContext | null = null;
    /** True once the latest resume has settled and no suspend came after it. */
    let awake = false;
    /** Counts resume/suspend requests: a resume settling after a later suspend must not mark the context awake. */
    let request = 0;
    /** The factory gave no context or threw — not tried again. */
    let unavailable = false;

    const create = (): AudioContext | null => {
        if (context === null && !unavailable) {
            try {
                context = factory();
            } catch (error) {
                console.warn('The audio context could not be created', error);
            }
            unavailable = context === null;
        }
        return context;
    };

    const resume = (ctx: AudioContext): Promise<void> => {
        const mine = ++request;
        try {
            return ctx.resume().then(() => {
                if (mine === request) {
                    awake = true;
                }
            }).catch((error: unknown) => {
                console.warn('The audio context could not be resumed', error);
            });
        } catch (error) {
            console.warn('The audio context could not be resumed', error);
            return Promise.resolve();
        }
    };

    /** Plays the tone; settles when it has ended. */
    const play = (ctx: AudioContext): Promise<void> => {
        const oscillator = ctx.createOscillator();
        const gain = ctx.createGain();
        const at = ctx.currentTime;
        oscillator.type = 'sine';
        oscillator.frequency.value = FREQUENCY_HZ;
        gain.gain.setValueAtTime(0, at);
        gain.gain.linearRampToValueAtTime(VOLUME, at + FADE_S);
        gain.gain.setValueAtTime(VOLUME, at + DURATION_S - FADE_S);
        gain.gain.linearRampToValueAtTime(0, at + DURATION_S);
        oscillator.connect(gain);
        gain.connect(ctx.destination);
        const ended = new Promise<void>((done) => {
            oscillator.onended = () => done();
            setTimeout(done, ENDED_FALLBACK_MS);
        });
        oscillator.start(at);
        oscillator.stop(at + DURATION_S);
        return ended;
    };

    return {
        prime(): void {
            const ctx = create();
            if (ctx !== null && ctx.state !== 'closed') {
                void resume(ctx);
            }
        },

        async beep(): Promise<void> {
            try {
                const ctx = context ?? (hasBeenActive() ? create() : null);
                if (ctx === null) {
                    return;
                }
                if (!awake || ctx.state !== 'running') {
                    await Promise.race([
                        resume(ctx),
                        new Promise<void>((done) => setTimeout(done, RESUME_WAIT_MS)),
                    ]);
                }
                if (ctx.state !== 'running') {
                    return;
                }
                await play(ctx);
            } catch (error) {
                console.warn('The timer tone could not be played', error);
            }
        },

        suspend(): void {
            request++;
            awake = false;
            if (context === null || context.state === 'closed') {
                return;
            }
            try {
                context.suspend().catch((error: unknown) => {
                    console.warn('The audio context could not be suspended', error);
                });
            } catch (error) {
                console.warn('The audio context could not be suspended', error);
            }
        },

        running(): boolean {
            return awake && context?.state === 'running';
        },
    };
}
